import { useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { ArrowUpRight } from "lucide-react";
import SEO from "@/components/seo/SEO";

const AdminLogin = () => {
  const { admin, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (admin && admin.role === "admin") {
    return <Navigate to="/admin" replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast.error("Enter your email and password.");
      return;
    }
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Welcome back.");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main
      data-testid="admin-login-page"
      className="min-h-screen bg-[#F7F7F4] flex items-center justify-center px-6 py-16"
    >
      <SEO title="Sign in" path="/admin/login" noindex />
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="font-sora text-[26px] font-extrabold tracking-tight text-[#111111] inline-block"
        >
          Vaymilo<span className="text-[#2563EB]">.</span>
        </Link>
        <div className="mt-2 overline">— Admin</div>

        <div className="mt-8 bg-white border border-[#E7E7E2] rounded-2xl p-8 md:p-10">
          <h1 className="font-sora text-[28px] md:text-[32px] font-bold text-[#111111] tracking-tight leading-tight">
            Sign in.
          </h1>
          <p className="mt-2 font-manrope text-[15px] text-[#111111]/65">
            Studio access only. This area is not public.
          </p>

          <form onSubmit={onSubmit} noValidate className="mt-8 space-y-5" data-testid="admin-login-form">
            <div>
              <label
                htmlFor="login-email"
                className="block font-manrope text-sm font-medium text-[#111111] mb-2"
              >
                Email
              </label>
              <input
                id="login-email"
                type="email"
                data-testid="input-admin-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                required
                className="w-full bg-white border border-[#E7E7E2] rounded-lg px-4 py-3 text-[15px] text-[#111111] placeholder:text-[#111111]/40 font-manrope focus:outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20 transition-all"
              />
            </div>
            <div>
              <label
                htmlFor="login-password"
                className="block font-manrope text-sm font-medium text-[#111111] mb-2"
              >
                Password
              </label>
              <input
                id="login-password"
                type="password"
                data-testid="input-admin-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                required
                className="w-full bg-white border border-[#E7E7E2] rounded-lg px-4 py-3 text-[15px] text-[#111111] placeholder:text-[#111111]/40 font-manrope focus:outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20 transition-all"
              />
            </div>

            <button
              type="submit"
              data-testid="btn-admin-login"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-60 text-white font-manrope font-medium text-[15px] px-6 py-3.5 rounded-lg transition-colors"
            >
              {loading ? "Signing in…" : "Sign in"}
              {!loading && <ArrowUpRight size={16} strokeWidth={2.25} />}
            </button>
          </form>
        </div>

        <Link
          to="/"
          className="mt-6 inline-block font-manrope text-sm text-[#111111]/60 hover:text-[#111111] transition-colors"
        >
          ← Back to site
        </Link>
      </div>
    </main>
  );
};

export default AdminLogin;
